import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ServiceData } from "../../types/service";
import { DateRange } from "react-date-range";
import { enUS } from "date-fns/locale";
import { observer } from "mobx-react-lite";
import { Calendar, Clock, Users, User, Mail, Phone } from "lucide-react";
import PhoneInput from "react-phone-number-input";

import {
  resortBookingSchema,
  ResortBookingSchema,
} from "../../lib/schemas/resortBookingSchema";
import { useStore } from "../../app/stores/store";

import LoadingSpinner from "../common/LoadingSpinner";
import ImageGallery from "./ImageGallery";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { toLocalISOString } from "../../lib/utils";

// Import UI Components
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

// Types for unavailable time slots
interface UnavailableTimeSlot {
  startDateTime: string;
  endDateTime: string;
}

type Props = {
  service: ServiceData;
};

const BookingResort = ({ service }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resortBookingStore } = useStore();

  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState<
    UnavailableTimeSlot[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeError, setTimeError] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const [checkInTime, setCheckInTime] = useState("14:00");
  const [checkOutTime, setCheckOutTime] = useState("20:00");

  const form = useForm<ResortBookingSchema>({
    resolver: zodResolver(resortBookingSchema),
    defaultValues: {
      adultsNumber: 1,
      childrenNumber: 0,
      serviceId: service.id,
      totalPrice: 0,
      customerName: "",
      email: "",
      phone: "",
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    control,
  } = form;

  // Fetch unavailable time slots from backend
  useEffect(() => {
    const fetchUnavailableTimeSlots = async () => {
      setIsLoading(true);
      try {
        // Set date range for the next 6 months
        const startDate = new Date();
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 6);

        const timeSlots = await resortBookingStore.getUnavailableDates(
          service.id,
          startDate,
          endDate
        );
        setUnavailableTimeSlots(timeSlots);
      } catch (error) {
        console.error("Failed to load unavailable time slots", error);
        toast.error("Failed to load availability data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnavailableTimeSlots();
  }, [service.id, resortBookingStore]);

  // Check if a specific date-time range conflicts with existing bookings
  const isDateTimeRangeAvailable = (
    startDateTime: Date,
    endDateTime: Date
  ): boolean => {
    for (const slot of unavailableTimeSlots) {
      const slotStart = new Date(slot.startDateTime); // These are UTC strings
      const slotEnd = new Date(slot.endDateTime);

      // Convert to same timezone if needed
      const start = new Date(startDateTime.toISOString());
      const end = new Date(endDateTime.toISOString());

      if (start < slotEnd && end > slotStart) {
        return false;
      }
    }
    return true;
  };

  // For calendar display - show dates as disabled only if they are completely booked
  // This is a simplified approach - you might want to adjust based on your business logic
  const isDateDisabled = (date: Date) => {
    return false; // Show all dates as available
  };
  const onDateRangeChange = (ranges: any) => {
    const { startDate, endDate } = ranges.selection;

    // Don't check for disabled dates, just set the selection
    setDateRange(ranges.selection);
  };

  const calculateTotalPrice = () => {
    const adults = watch("adultsNumber") || 0;
    const children = watch("childrenNumber") || 0;
    const price = (adults + children) * Number(service.price);
    setTotalPrice(price);
    setValue("totalPrice", price);
  };

  useEffect(() => {
    setValue("checkInDate", dateRange.startDate.toISOString());
    setValue("checkOutDate", dateRange.endDate.toISOString());
    setValue("checkInTime", checkInTime);
    setValue("checkOutTime", checkOutTime);
  }, [
    dateRange.startDate,
    dateRange.endDate,
    checkInTime,
    checkOutTime,
    setValue,
  ]);

  useEffect(() => {
    calculateTotalPrice();
  }, [watch("adultsNumber"), watch("childrenNumber")]);

  const validateTimeDifference = (checkIn: string, checkOut: string) => {
    const [checkInHour, checkInMinute] = checkIn.split(":").map(Number);
    const [checkOutHour, checkOutMinute] = checkOut.split(":").map(Number);

    const checkInDate = new Date(dateRange.startDate);
    checkInDate.setHours(checkInHour, checkInMinute, 0, 0);

    const checkOutDate = new Date(dateRange.startDate);
    checkOutDate.setHours(checkOutHour, checkOutMinute, 0, 0);

    if (checkOutDate <= checkInDate) {
      checkOutDate.setDate(checkOutDate.getDate() + 1);
    }

    const diffMs = checkOutDate.getTime() - checkInDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    return diffHours >= 6;
  };

  const handleCheckInTimeChange = (value: string) => {
    const [h, m] = value.split(":").map(Number);
    const tempStart = new Date(dateRange.startDate);
    tempStart.setHours(h, m, 0, 0);

    const [oh, om] = checkOutTime.split(":").map(Number);
    const tempEnd = new Date(dateRange.endDate);
    tempEnd.setHours(oh, om, 0, 0);

    if (tempEnd <= tempStart) {
      tempEnd.setDate(tempEnd.getDate() + 1);
    }

    // Check availability first
    if (!isDateTimeRangeAvailable(tempStart, tempEnd)) {
      setTimeError(
        "This time slot is not available. Please choose different times."
      );
    } else if (!validateTimeDifference(value, checkOutTime)) {
      setTimeError("Minimum booking duration is 6 hours.");
    } else {
      setTimeError("");
    }

    setCheckInTime(value);
  };

  const handleCheckOutTimeChange = (value: string) => {
    const [h, m] = value.split(":").map(Number);
    const tempEnd = new Date(dateRange.endDate);
    tempEnd.setHours(h, m, 0, 0);

    const [ih, im] = checkInTime.split(":").map(Number);
    const tempStart = new Date(dateRange.startDate);
    tempStart.setHours(ih, im, 0, 0);

    if (tempEnd <= tempStart) {
      tempEnd.setDate(tempEnd.getDate() + 1);
    }

    // Check availability first
    if (!isDateTimeRangeAvailable(tempStart, tempEnd)) {
      setTimeError(
        "This time slot is not available. Please choose different times."
      );
    } else if (!validateTimeDifference(checkInTime, value)) {
      setTimeError("Minimum booking duration is 6 hours.");
    } else {
      setTimeError("");
    }

    setCheckOutTime(value);
  };

  const calculateMinimumCheckOutTime = (checkIn: string) => {
    const [checkInHour, checkInMinute] = checkIn.split(":").map(Number);
    const checkInDate = new Date(dateRange.startDate);
    checkInDate.setHours(checkInHour, checkInMinute, 0, 0);

    const minCheckOutDate = new Date(
      checkInDate.getTime() + 6 * 60 * 60 * 1000
    );
    const hours = minCheckOutDate.getHours().toString().padStart(2, "0");
    const minutes = minCheckOutDate.getMinutes().toString().padStart(2, "0");

    return `${hours}:${minutes}`;
  };

  const onSubmit = async (data: ResortBookingSchema) => {
    setIsSubmitting(true);

    // These checks are now redundant since Zod schema ensures they exist
    // But you can keep them for extra safety
    if (!data.checkInDate || !data.checkOutDate) {
      toast.error("Check-in and check-out dates are required.");
      setIsSubmitting(false);
      return;
    }

    if (!data.checkOutTime || !data.checkInTime) {
      toast.error("Check-in and check-out times are required");
      setIsSubmitting(false);
      return;
    }

    const [checkInHour, checkInMinute] = data.checkInTime
      .split(":")
      .map(Number);
    const [checkOutHour, checkOutMinute] = data.checkOutTime
      .split(":")
      .map(Number);

    // Now TypeScript knows these are definitely strings
    const startDateTime = new Date(data.checkInDate);
    const endDateTime = new Date(data.checkOutDate);

    startDateTime.setHours(checkInHour, checkInMinute, 0, 0);
    endDateTime.setHours(checkOutHour, checkOutMinute, 0, 0);

    const durationHours =
      (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    if (durationHours < 6) {
      toast.error("Booking must be at least 6 hours long.");
      setIsSubmitting(false);
      return;
    }

    // Final availability check using time slots
    if (!isDateTimeRangeAvailable(startDateTime, endDateTime)) {
      toast.error(
        "Selected time slot is no longer available. Please choose different dates or times."
      );
      setIsSubmitting(false);
      return;
    }

    const bookingPayload = {
      customerName: data.customerName,
      phone: data.phone,
      email: data.email,
      adultsNumber: data.adultsNumber,
      childrenNumber: data.childrenNumber || 0,
      totalPrice: totalPrice,
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      checkInTime: checkInTime,
      checkOutTime: checkOutTime,
      serviceId: service.id,
      paymentType: null,
      paymentId: null,
      invoiceNo: "",
    };

    const result = await resortBookingStore.addBooking(bookingPayload);

    if (result.status === "success") {
      toast.success(t("Booking added successfully"));
      const sessionId = resortBookingStore.getOrCreateSessionId();
      resortBookingStore.setSessionData(sessionId, [result.data]);
      navigate(`/listings/booking/resort/${result.data}`);
    } else {
      toast.error(`${t("Error")}: ${result.error}`);
    }

    setIsSubmitting(false);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-0" noValidate>
        {/* Date Picker with responsive wrapper */}
        <FormItem className="space-y-1">
          <FormLabel className="flex items-center gap-2 text-sm border-box">
            <Calendar className="w-2 h-2" />
            Booking Dates
          </FormLabel>

          {/* Calendar Container with responsive constraints */}
          <div className="w-full overflow-x-hidden pb-2">
            <div className="w-full mx-auto">
              <DateRange
                ranges={[dateRange]}
                onChange={onDateRangeChange}
                locale={enUS}
                moveRangeOnFirstSelection={false}
                rangeColors={["#e71875"]}
                disabledDay={isDateDisabled}
                className="w-full min-w-full"
              />
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-1">
            All dates are available. Time slot availability will be checked when
            you select your times.
          </p>
        </FormItem>

        {/* Time Inputs - Keep in same row but make responsive */}
        <div className="grid grid-cols-2 gap-3">
          <FormItem className="space-y-1">
            <FormLabel className="flex items-center gap-2 text-sm whitespace-nowrap">
              <Clock className="w-3 h-3 flex-shrink-0" />
              Check-in Time
            </FormLabel>
            <FormControl>
              <Input
                type="time"
                value={checkInTime}
                onChange={(e) => handleCheckInTimeChange(e.target.value)}
                className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] h-9 text-sm w-full min-w-0"
                required
              />
            </FormControl>
          </FormItem>

          <FormItem className="space-y-1">
            <FormLabel className="flex items-center gap-2 text-sm whitespace-nowrap">
              <Clock className="w-3 h-3 flex-shrink-0" />
              Check-out Time
            </FormLabel>
            <FormControl>
              <Input
                type="time"
                value={checkOutTime}
                onChange={(e) => handleCheckOutTimeChange(e.target.value)}
                className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] h-9 text-sm w-full min-w-0"
                required
              />
            </FormControl>
            {timeError && (
              <FormMessage className="text-red-500 text-xs break-words">
                {timeError}
              </FormMessage>
            )}
            {!timeError && checkInTime && (
              <p className="text-xs text-gray-500 break-words">
                Minimum: {calculateMinimumCheckOutTime(checkInTime)}
              </p>
            )}
          </FormItem>
        </div>

        {/* Adults/Children - Keep in same row */}
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="adultsNumber"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="flex items-center gap-2 text-sm whitespace-nowrap">
                  <Users className="w-3 h-3 flex-shrink-0" />
                  Adults
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 1)
                    }
                    className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] h-9 text-sm w-full min-w-0"
                  />
                </FormControl>
                <FormMessage className="text-red-500 text-xs break-words" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="childrenNumber"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="text-sm whitespace-nowrap">
                  Children
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                    className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] h-9 text-sm w-full min-w-0"
                  />
                </FormControl>
                <FormMessage className="text-red-500 text-xs break-words" />
              </FormItem>
            )}
          />
        </div>

        {/* Customer Information - Single column fields */}
        <FormField
          control={form.control}
          name="customerName"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="flex items-center gap-2 text-sm">
                <User className="w-3 h-3 flex-shrink-0" />
                Full Name
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your name"
                  {...field}
                  className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] h-9 text-sm w-full"
                />
              </FormControl>
              <FormMessage className="text-red-500 text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="flex items-center gap-2 text-sm">
                <Mail className="w-3 h-3 flex-shrink-0" />
                Email
              </FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  {...field}
                  className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] h-9 text-sm w-full"
                />
              </FormControl>
              <FormMessage className="text-red-500 text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="text-sm">Phone</FormLabel>
              <FormControl>
                <div className="rounded-md border border-input focus-within:border-[#e71875] transition-colors duration-200 w-full">
                  <PhoneInput
                    international
                    defaultCountry="AE"
                    placeholder="Enter phone number"
                    value={field.value}
                    onChange={field.onChange}
                    className="[&>input]:border-0 [&>input]:ring-0 [&>input]:outline-none [&>input]:focus:ring-0 [&>input]:focus:outline-none flex items-center w-full bg-background px-3 py-2 text-sm"
                  />
                </div>
              </FormControl>
              <FormMessage className="text-red-500 text-xs" />
            </FormItem>
          )}
        />

        {/* Total Price */}
        <div className="flex justify-between items-center pt-3 border-t">
          <span className="font-semibold text-base">Total Price:</span>
          <span className="font-bold text-base text-[#e71875]">
            {totalPrice.toFixed(2)} AED
          </span>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting || !!timeError}
          className="w-full bg-[#e71875] hover:bg-[#d4166a] text-white py-4 text-base"
        >
          {isSubmitting
            ? "Processing..."
            : `Book Now - ${totalPrice.toFixed(2)} AED`}
        </Button>
      </form>
    </Form>
  );
};

export default observer(BookingResort);
